import React, { useEffect, useState, useCallback } from "react";
import { lineage } from "@/lib/lineage";
import { PersonPicker } from "@/components/PersonPicker";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LoadError } from "@/components/LoadError";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { TreeView } from "@/components/TreeView";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PersonForm } from "@/components/PersonForm";
import { EditableTextBlock } from "@/components/EditableTextBlock";
import { UserPlus, Trees as TreesIcon } from "lucide-react";

export const TreePage = () => {
  const { API, user } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [clans, setClans] = useState([]);
  const [persons, setPersons] = useState([]);
  const [filter, setFilter] = useState("__all");
  const [lineMode, setLineMode] = useState("full");
  const [rootId, setRootId] = useState("");
  const [open, setOpen] = useState(false);

  const [loadError, setLoadError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const [cl, tr] = await Promise.all([
        axios.get(`${API}/clans`, { timeout: 20000 }),
        axios.get(`${API}/tree`, { params: filter === "__all" ? {} : { clan_key: filter }, timeout: 30000 }),
      ]);
      setClans(cl.data);
      setPersons(tr.data.persons);
      setLoaded(true);
    } catch { setLoadError(true); }
  }, [API, filter]);

  useEffect(() => { load(); }, [load]);

  const isLogged = user && user !== false;

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10" data-testid="tree-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 fade-in-up">
        <div>
          <div className="label-uppercase text-[#993D22] mb-2">{t("tree")}</div>
          <h1 className="font-serif-heritage text-5xl text-[#261911]">{t("treeTitle")}</h1>
          <p className="text-sm text-[#6E5B4F] mt-2">
            {lang === "fr"
              ? "Cliquez sur une personne pour voir sa fiche détaillée."
              : "Click a person to see their detailed profile."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56 bg-[#FAF7F2]" data-testid="tree-clan-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{t("allClans")}</SelectItem>
              {clans.map(c => (
                <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isLogged && (
            <Button className="btn-terra" onClick={() => setOpen(true)} data-testid="tree-add-person-btn">
              <UserPlus className="w-4 h-4 mr-2" /> {t("addPerson")}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="lineage-filters">
        <select value={lineMode} onChange={e => setLineMode(e.target.value)} className="h-9 rounded-md border border-input bg-[#FAF7F2] px-3 text-sm" data-testid="lineage-mode">
          <option value="full">Arbre complet</option>
          <option value="paternal">Lignée paternelle</option>
          <option value="maternal">Lignée maternelle</option>
        </select>
        {lineMode !== "full" && (
          <div className="w-72"><PersonPicker value={rootId} onChange={setRootId} options={persons} testid="lineage-root" placeholder="Choisir la personne de départ" allowCreate={false} /></div>
        )}
        {lineMode !== "full" && !rootId && <span className="text-xs text-[#6E5B4F]">Choisissez une personne pour afficher sa lignée.</span>}
      </div>

      {loadError ? <LoadError onRetry={load} /> : !loaded ? (
        <div className="heritage-card p-10 text-center text-[#6E5B4F]" data-testid="tree-loading">Chargement de l'arbre…</div>
      ) : (
        <TreeView
          persons={lineage(persons, rootId, lineMode)}
          clans={clans}
          onPersonClick={(p) => nav(`/persons/${p.id}`)}
        />
      )}

      {filter === "__all" && clans.length > 0 && (
        <section className="mt-10 heritage-card p-6" data-testid="mvog-koe-overview">
          <div className="flex items-center gap-2 mb-4">
            <TreesIcon className="w-5 h-5 text-[#993D22]" />
            <div className="label-uppercase text-[#993D22]">{t("mvogKoeHeader")}</div>
          </div>
          <p className="text-sm text-[#6E5B4F] mb-6 max-w-2xl">
            {t("globalTree")} — {t("byFamily")}. Toutes les familles du village descendent de la lignée commune <b>Mvog Koé</b>.
          </p>          <svg viewBox="0 0 900 320" width="100%" style={{ maxHeight: 320 }}>
            {/* root node */}
            <g>
              <circle cx={450} cy={40} r={34} fill="#993D22" />
              <text x={450} y={46} textAnchor="middle" fill="#FAF7F2" fontFamily="'Cormorant Garamond', serif" fontSize={18} fontWeight={700}>Mvog Koé</text>
            </g>
            {/* 5 child clans */}
            {clans.map((c, i) => {
              const total = clans.length;
              const startX = 70;
              const endX = 830;
              const x = total === 1 ? (startX + endX) / 2 : startX + (i * (endX - startX)) / (total - 1);
              const y = 240;
              return (
                <g key={c.key} onClick={() => nav(`/clans/${c.key}`)} style={{ cursor: "pointer" }} data-testid={`overview-clan-${c.key}`}>
                  <path d={`M 450 74 C 450 160, ${x} 160, ${x} ${y - 42}`} stroke="#8B6B55" strokeWidth={1.4} fill="none" opacity={0.6} />
                  <circle cx={x} cy={y} r={36} fill="#FAF7F2" stroke={c.color} strokeWidth={2.5} />
                  <text x={x} y={y - 2} textAnchor="middle" fill={c.color} fontFamily="'Cormorant Garamond', serif" fontSize={14} fontWeight={700}>
                    {c.name.split(" ")[1] || c.name}
                  </text>
                  <text x={x} y={y + 14} textAnchor="middle" fill="#6E5B4F" fontSize={9} letterSpacing={2}>
                    {c.member_count || 0}
                  </text>
                  {/* second word for compound names */}
                  {c.name.split(" ").length > 2 && (
                    <text x={x} y={y + 56} textAnchor="middle" fill={c.color} fontSize={10} fontWeight={600}>
                      {c.name.split(" ").slice(2).join(" ")}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </section>
      )}

      {filter === "__all" && (
        <div className="mt-10">
          <EditableTextBlock
            settingKey="mvog_koe_ancestors"
            title={t("ancestralLine")}
            description={t("ancestralLineDesc")}
            placeholder="Mvog Koé est fils de… lui-même descendant de…"
            canEdit={user && user !== false && (user.role === "elder" || user.role === "admin")}
            testIdPrefix="ancestral-line"
            emptyText={t("emptyAncestralLine")}
          />
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">{t("newPerson")}</DialogTitle>
          </DialogHeader>
          <PersonForm
            initial={filter !== "__all" ? { clan_key: filter } : {}}
            clans={clans}
            allPersons={persons}
            onDone={() => { setOpen(false); load(); }}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TreePage;
